import React, { useRef, useState } from 'react';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import useTheme from '@hero/branding/theme';
import { MarkButton } from '../../../components/MarkControls';
import type { SegmentAttribute, SegmentFilter } from '../../../types';
import type { KeyedFilter } from '../../../hooks/useSegmentDraft';
import FilterRow from './FilterRow';
import FilterEditorModal from './FilterEditorModal';
import * as styles from './style.module.css';

type Props = {
    filters: KeyedFilter[];
    attributes: SegmentAttribute[];
    onUpdate: (index: number, updated: SegmentFilter) => void;
    onRemove: (index: number) => void;
    onAdd: (filter: SegmentFilter) => void;
    disabled?: boolean;
    disabledReason?: string;
    idPrefix: string;
};

const FilterList: React.FC<Props> = ({
    filters,
    attributes,
    onUpdate,
    onRemove,
    onAdd,
    disabled,
    disabledReason,
    idPrefix
}) => {
    const { colors, borders } = useTheme();
    const containerRef = useRef<HTMLDivElement>(null);
    const addButtonRef = useRef<HTMLButtonElement>(null);
    const [editor, setEditor] = useState<{ key: number | null; filter?: SegmentFilter } | null>(
        null
    );

    const handleRemove = (index: number) => {
        onRemove(index);
        requestAnimationFrame(() => {
            const nextIndex = Math.min(index, filters.length - 2);
            const next = containerRef.current?.querySelector<HTMLButtonElement>(
                `[data-filter-row="${nextIndex}"] button`
            );
            (next ?? addButtonRef.current)?.focus();
        });
    };

    return (
        <div ref={containerRef} className={styles.filterList}>
            {filters.length === 0 && (
                <Section
                    padding="regular"
                    border
                    className={styles.emptyState}
                    styles={{ ...borders.variant.secondary, borderStyle: 'dashed' }}
                >
                    <P noDefaultMargin strong>
                        No filters yet
                    </P>
                    <P noDefaultMargin size="small" styles={{ color: colors.neutrals.textBeta }}>
                        {disabled
                            ? (disabledReason ?? 'Filter fields are unavailable.')
                            : 'Add your first filter to define who belongs in this segment.'}
                    </P>
                </Section>
            )}
            {filters.map((filter, index) => (
                <FilterRow
                    key={filter._key}
                    filter={filter}
                    index={index}
                    attributes={attributes}
                    onEdit={() => setEditor({ key: filter._key, filter })}
                    onRemove={handleRemove}
                    idPrefix={idPrefix}
                />
            ))}
            <div>
                <MarkButton
                    ref={addButtonRef}
                    onClick={() => setEditor({ key: null })}
                    disabled={disabled}
                    variant="outlineSecondary"
                >
                    + Add filter
                </MarkButton>
            </div>
            {editor && (
                <FilterEditorModal
                    initialFilter={editor.filter}
                    attributes={attributes}
                    onClose={() => setEditor(null)}
                    onSave={(filter) => {
                        if (editor.key === null) onAdd(filter);
                        else {
                            const index = filters.findIndex(
                                (candidate) => candidate._key === editor.key
                            );
                            if (index !== -1) onUpdate(index, filter);
                        }
                        setEditor(null);
                    }}
                />
            )}
        </div>
    );
};

export default FilterList;
